import React, { useState, useEffect } from 'react';
import { Button, Form, Card, Row, Col, Spinner, Alert, Tooltip } from 'react-bootstrap';
import { SketchPicker } from 'react-color';
import MediaSelector from '../Media/MediaSelector';
import { SettingsService, PageService } from '../../../Services/Axios/Api';
import { ToastContainer } from 'react-toastify';
import { toast } from 'react-toastify';
const initialSettings = {
  Title: '',
  DomainName: '',
  HomePageId: 0,
  Logo: '',
  FavIcon: '',
  PrimaryColor: '#1976d2',
  SecondaryColor: '#dc004e',
  ContactEmail: '',
  ContactPhone: '',
  Address: '',
  SmtpServer: '',
  SmtpPort: '',
  SmtpUseSsl: false,
  SmtpUsername: '',
  SmtpPassword: '',
  SmtpFromEmail: '',
  SmtpFromName: '',
};

const SETTINGS_ID = 1;

const apiToUi = (api) => ({
  Title: api.title || '',
  DomainName: api.domainName || '',
  HomePageId: api.homePageId || 0,
  Logo: api.logo || '',
  FavIcon: api.favIcon || '',
  PrimaryColor: api.primaryColor || '#1976d2',
  SecondaryColor: api.secondaryColor || '#dc004e',
  ContactEmail: api.contactEmail || '',
  ContactPhone: api.contactPhone || '',
  Address: api.address || '',
  SmtpServer: api.smtpServer || '',
  SmtpPort: api.smtpPort || '',
  SmtpUseSsl: api.smtpUseSsl || false,
  SmtpUsername: api.smtpUsername || '',
  SmtpPassword: api.smtpPassword || '',
  SmtpFromEmail: api.smtpFromEmail || '',
  SmtpFromName: api.smtpFromName || '',
});

const uiToApi = (ui) => ({
  title: ui.Title,
  domainName: ui.DomainName,
  homePageId: ui.HomePageId||0,
  logo: ui.Logo,
  favIcon: ui.FavIcon,
  primaryColor: ui.PrimaryColor,
  secondaryColor: ui.SecondaryColor,
  contactEmail: ui.ContactEmail,
  contactPhone: ui.ContactPhone,
  address: ui.Address,
  smtpServer: ui.SmtpServer,
  smtpPort: ui.SmtpPort,
  smtpUseSsl: ui.SmtpUseSsl,
  smtpUsername: ui.SmtpUsername,
  smtpPassword: ui.SmtpPassword,
  smtpFromEmail: ui.SmtpFromEmail,
  smtpFromName: ui.SmtpFromName,
});

const GeneralInfo = () => {
  const [settings, setSettings] = useState(initialSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showLogoSelector, setShowLogoSelector] = useState(false);
  const [showFavSelector, setShowFavSelector] = useState(false);
  const [showPrimaryColorPicker, setShowPrimaryColorPicker] = useState(false);
  const [showSecondaryColorPicker, setShowSecondaryColorPicker] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [pages, setPages] = useState([]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const fetchSettings = async () => {
      try {
        const response = await SettingsService.getSettings(SETTINGS_ID);
        setSettings(apiToUi(response.result));
        setLoading(false);
      } catch (err) {
        setError(err.message || 'Failed to load settings');
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    const fetchPages = async () => {
      try {
        const response = await PageService.GetAllPages();
        if (response.isSuccess) {
          setPages(response.result || []);
        }
      } catch (err) {
        console.error('Failed to load pages:', err);
      }
    };
    fetchPages();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleColorChange = (color, field) => {
    setSettings((prev) => ({ ...prev, [field]: color.hex }));
  };

  const handleLogoSelect = (path) => {
    setSettings((prev) => ({ ...prev, Logo: path }));
    setShowLogoSelector(false);
  };

  const handleFavSelect = (path) => {
    setSettings((prev) => ({ ...prev, FavIcon: path }));
    setShowFavSelector(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await SettingsService.updateSettings(SETTINGS_ID, uiToApi(settings));
      setSuccess(true);
      toast.success('Settings saved successfully!');
    } catch (err) {
      setError( 'Failed to save settings');
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    setTestingEmail(true);
    try {
      await SettingsService.testEmail();
      toast.success('Test email sent successfully!');
    } catch (err) {
      toast.error(err.message || 'Failed to send test email');
    } finally {
      setTestingEmail(false);
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <Form onSubmit={handleSubmit}>
      <ToastContainer />
      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">Settings saved successfully!</Alert>}
      <Card className="mb-3">
        <Card.Header>General</Card.Header>
        <Card.Body>
          <Form.Group className="mb-3">
            <Form.Label>Title</Form.Label>
            <Form.Control
              type="text"
              name="Title"
              value={settings.Title}
              onChange={handleChange}
              maxLength={255}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Domain Name</Form.Label>
            <Form.Control
              type="text"
              name="DomainName"
              placeholder="https://www.example.com"
              value={settings.DomainName}
              onChange={handleChange}
              maxLength={255}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Home Page</Form.Label>
            <Form.Select
              name="HomePageId"
              value={settings.HomePageId}
              onChange={handleChange}
            >
              <option value={0} >Default</option>
              {pages.map((page) => (
                <option key={page.id} value={page.id}>
                  {page.title || page.slug || `Page ${page.id}`}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          
          <Row>
            <Col md={6} className="mb-3">
              <Form.Label>Logo</Form.Label>
              <div className="d-flex align-items-center gap-2">
                {settings.Logo ? (
                  <img src={settings.Logo} alt="Logo" style={{ maxHeight: 40, maxWidth: 80, objectFit: 'contain' }} />
                ) : (
                  <span className="text-muted">No logo</span>
                )}
                <Button size="sm" variant="outline-primary" onClick={() => setShowLogoSelector(true)}>
                  Select
                </Button>
                {settings.Logo && (
                  <Button size="sm" variant="outline-danger" onClick={() => setSettings((prev) => ({ ...prev, Logo: '' }))}>
                    Remove
                  </Button>
                )}
              </div>
            </Col>
            <Col md={6} className="mb-3">
              <Form.Label>FavIcon</Form.Label>
              <div className="d-flex align-items-center gap-2">
                {settings.FavIcon ? (
                  <img src={settings.FavIcon} alt="FavIcon" style={{ maxHeight: 32, maxWidth: 32, objectFit: 'contain' }} />
                ) : (
                  <span className="text-muted">No favicon</span>
                )}
                <Button size="sm" variant="outline-primary" onClick={() => setShowFavSelector(true)}>
                  Select
                </Button>
                {settings.FavIcon && (
                  <Button size="sm" variant="outline-danger" onClick={() => setSettings((prev) => ({ ...prev, FavIcon: '' }))}>
                    Remove
                  </Button>
                )}
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={6} className="mb-3">
              <Form.Label>Primary Color</Form.Label>
              <div className="d-flex align-items-center gap-2">
                <div
                  style={{ width: 30, height: 30, backgroundColor: settings.PrimaryColor, border: '1px solid #ccc', cursor: 'pointer' }}
                  onClick={() => setShowPrimaryColorPicker((v) => !v)}
                />
                <span>{settings.PrimaryColor}</span>
              </div>
              {showPrimaryColorPicker && (
                <div style={{ position: 'absolute', zIndex: 2 }}>
                  <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, left: 0 }} onClick={() => setShowPrimaryColorPicker(false)} />
                  <SketchPicker color={settings.PrimaryColor} onChange={(color) => handleColorChange(color, 'PrimaryColor')} />
                </div>
              )}
            </Col>
            <Col md={6} className="mb-3">
              <Form.Label>Secondary Color</Form.Label>
              <div className="d-flex align-items-center gap-2">
                <div
                  style={{ width: 30, height: 30, backgroundColor: settings.SecondaryColor, border: '1px solid #ccc', cursor: 'pointer' }}
                  onClick={() => setShowSecondaryColorPicker((v) => !v)}
                />
                <span>{settings.SecondaryColor}</span>
              </div>
              {showSecondaryColorPicker && (
                <div style={{ position: 'absolute', zIndex: 2 }}>
                  <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, left: 0 }} onClick={() => setShowSecondaryColorPicker(false)} />
                  <SketchPicker color={settings.SecondaryColor} onChange={(color) => handleColorChange(color, 'SecondaryColor')} />
                </div>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>
      <Card className="mb-3">
        <Card.Header>Contact Information</Card.Header>
        <Card.Body>
          <Row>
            <Col md={6} className="mb-3">
              <Form.Label>Contact Email</Form.Label>
              <Form.Control
                type="email"
                name="ContactEmail"
                value={settings.ContactEmail}
                onChange={handleChange}
                maxLength={100}
              />
              <p className="text-muted p-2">This email address will receive emails sent by the system to users.</p>
            </Col>
            <Col md={6} className="mb-3">
              <Form.Label>Contact Phone</Form.Label>
              <Form.Control
                type="text"
                name="ContactPhone"
                value={settings.ContactPhone}
                onChange={handleChange}
                maxLength={20}
              />
            </Col>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label>Address</Form.Label>
            <Form.Control
              type="text"
              name="Address"
              value={settings.Address}
              onChange={handleChange}
              maxLength={255}
            />
          </Form.Group>
        </Card.Body>
      </Card>
      <Card className="mb-3">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            
            <div>Email Configuration</div>  
              <div className="d-flex justify-content-end">
            <Button variant="secondary" onClick={handleTestEmail} disabled={testingEmail}>
            {testingEmail ? <Spinner animation="border" size="sm" className="me-2" /> : null}
            Test Email
          </Button>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6} className="mb-3">
              <Form.Label>SMTP Server</Form.Label>
              <Form.Control
                type="text"
                name="SmtpServer"
                value={settings.SmtpServer}
                onChange={handleChange}
                maxLength={100}
              />
            </Col>
            <Col md={3} className="mb-3">
              <Form.Label>SMTP Port</Form.Label>
              <Form.Control
                type="number"
                name="SmtpPort"
                value={settings.SmtpPort}
                onChange={handleChange}
              />
            </Col>
            <Col md={3} className="mb-3 d-flex align-items-end">
              <Form.Check
                type="checkbox"
                label="Use SSL"
                name="SmtpUseSsl"
                checked={settings.SmtpUseSsl}
                onChange={handleChange}
              />
            </Col>
          </Row>
          <Row>
            <Col md={6} className="mb-3">
              <Form.Label>SMTP Username</Form.Label>
              <Form.Control
                type="text"
                name="SmtpUsername"
                value={settings.SmtpUsername}
                onChange={handleChange}
                maxLength={100}
              />
            </Col>
            <Col md={6} className="mb-3">
              <Form.Label>SMTP Password</Form.Label>
              <Form.Control
                type="password"
                name="SmtpPassword"
                value={settings.SmtpPassword}
                onChange={handleChange}
                maxLength={100}
              />
            </Col>
          </Row>
          <Row>
            <Col md={6} className="mb-3">
              <Form.Label>SMTP From Email</Form.Label>
              <Form.Control
                type="email"
                name="SmtpFromEmail"
                value={settings.SmtpFromEmail}
                onChange={handleChange}
                maxLength={100}
              />
            </Col>
            <Col md={6} className="mb-3">
              <Form.Label>SMTP From Name</Form.Label>
              <Form.Control
                type="text"
                name="SmtpFromName"
                value={settings.SmtpFromName}
                onChange={handleChange}
                maxLength={100}
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>
      
      <div className="d-flex justify-content-end">
        <Button type="submit" variant="primary" disabled={saving}>
          {saving ? <Spinner animation="border" size="sm" className="me-2" /> : null}
          Save Settings
        </Button>
      </div>
      {/* Media Selectors */}
      <MediaSelector isOpen={showLogoSelector} onClose={() => setShowLogoSelector(false)} onSelect={handleLogoSelect} />
      <MediaSelector isOpen={showFavSelector} onClose={() => setShowFavSelector(false)} onSelect={handleFavSelect} />
    </Form>
  );
};

export default GeneralInfo; 